<script setup lang="ts">
import { bemBuilder, debounce } from "@chatfood/core-utils";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import type { ISatButtonCurrentLocationEmits } from "@chatfood/design-system";
import {
  AtomText,
  AtomInput,
  AtomBadge,
  AtomLoadingSpinner,
  WoLocation,
  SatButton,
  SatFormControl,
  SatButtonCurrentLocation,
} from "@chatfood/design-system";
import type { IPickupLocationsProps } from "./props";
import type { IPickupLocationsEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import type { IDomainOutletState } from "~/domain/outlet-state";
import type { IDomainOutletPickup } from "~/domain/outlet-pickup";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";
import { usePublicConfig } from "~/config";
import { useDirectBackend } from "#imports";

const css = bemBuilder("pickup-locations");

const props = defineProps<IPickupLocationsProps>();
const emits = defineEmits<IPickupLocationsEmits>();

const { t } = useLocale();
const { outletRepo } = useDirectBackend();
const { outletStateMap, packagingLabel } = useDomainLabel();

const business = useBusinessStore();

const isLoading = ref(true);
const isLoadingMore = ref(false);
const isLoadingGeolocation = ref(false);

const latitude = ref<number>();
const longitude = ref<number>();

const mapState: Record<IDomainOutletState, (typeof WoLocation)["state"]> = {
  open: "open",
  closed: "close",
  pre_order_only: "soon",
};

async function initGeolocation() {
  isLoadingGeolocation.value = true;

  try {
    const res = await findPosition();
    if (res) setLocation(res.coords);
  } catch {
    // silent geolocation error
  } finally {
    isLoadingGeolocation.value = false;
  }
}

function findPosition(): Promise<GeolocationPosition> | undefined {
  const geolocation = window.navigator?.geolocation;

  if (!geolocation) return;

  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
    });
  });
}

function setLocation(coords: Partial<GeolocationCoordinates>) {
  if (
    !coords.latitude ||
    !coords.longitude ||
    (latitude.value === coords.latitude && longitude.value === coords.longitude)
  ) {
    return;
  }

  latitude.value = coords.latitude;
  longitude.value = coords.longitude;

  emits("geoLocationObtained", {
    lat: coords.latitude,
    lng: coords.longitude,
  });

  getLocations(true);
}

const currentPage = ref(1);
const hasMorePages = ref(false);
const LOCATIONS_PER_PAGE = 20;

const locations = ref<IDomainOutletPickup[]>([]);
const loadMoreRef = ref<HTMLElement>();
const observer = ref<IntersectionObserver>();

const getLocations = async (reset = false) => {
  if (reset) {
    isLoading.value = true;
    currentPage.value = 1;
    locations.value = [];
    hasMorePages.value = false;
  } else {
    isLoadingMore.value = true;
  }

  try {
    const res = await outletRepo.getPickupOutlets({
      businessId: business.id,
      salesChannel: business.salesChannel,
      lat: latitude.value,
      lng: longitude.value,
      ...(search.value && { name: search.value }),
      page: currentPage.value,
      limit: LOCATIONS_PER_PAGE,
    });

    locations.value = reset ? res.data : [...locations.value, ...res.data];
    hasMorePages.value = currentPage.value < res.meta.to;
    emits("loadedLocations", locations.value);
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
    if (reset) resetObserver();
  }
};

const loadMoreLocations = async () => {
  if (!hasMorePages.value || isLoadingMore.value) return;

  currentPage.value += 1;
  await getLocations(false);
};

const onIntersect = (entries: IntersectionObserverEntry[]) => {
  if (entries[0].isIntersecting && hasMorePages.value && !isLoadingMore.value) {
    loadMoreLocations();
  }
};

const setupIntersectionObserver = () => {
  if (!loadMoreRef.value || observer.value) return;
  observer.value = new IntersectionObserver(onIntersect, {
    rootMargin: "100px",
    threshold: 0.1,
  });
  observer.value.observe(loadMoreRef.value);
};

const cleanupObserver = () => {
  observer.value?.disconnect();
  observer.value = undefined;
};

const resetObserver = () => {
  cleanupObserver();
  nextTick(setupIntersectionObserver);
};

const hasPackaging = computed(() =>
  locations.value.some((l) => l.acceptsPackaging),
);

onMounted(() => {
  Promise.all([initGeolocation(), getLocations(true)]).then(() => {
    nextTick(() => {
      setupIntersectionObserver();
    });
  });
});

onUnmounted(() => {
  cleanupObserver();
});

const onClickLocation = (location: IDomainOutletPickup) => {
  emits("clickedLocation", location);
};

const focusedLocation = ref();
function onFocusLocation(location: IDomainOutletPickup) {
  focusedLocation.value = location;
  emits("focusedLocation", focusedLocation.value);
}

function onBlurLocation() {
  focusedLocation.value = undefined;
}

const distanceFormatted = (distance?: number) => {
  if (typeof distance !== "number") return undefined;

  const distanceKm = distance / 1000;

  return distanceKm < 1
    ? t("components.pickup_locations.x_meters", {
        distance: Math.floor(distance),
      })
    : t("components.pickup_locations.x_kilometers", {
        distance: Math.floor(distanceKm),
      });
};

const { googleMapsApiKey } = usePublicConfig();

const onCurrentLocation = (
  location: ISatButtonCurrentLocationEmits["success"][0],
) => {
  setLocation({
    latitude: location.coordinates.lat,
    longitude: location.coordinates.lng,
  });
};

const search = ref("");
const debouncedgetLocations = debounce(() => getLocations(true), 500);

watch(
  [() => business.salesChannel, search],
  ([newChannel, newSearch], [oldChannel, oldSearch]) => {
    if (newChannel !== oldChannel || newSearch !== oldSearch) {
      debouncedgetLocations();
    }
  },
);

watch([() => locations.value.length, hasMorePages], () => {
  nextTick(() => {
    if (loadMoreRef.value && hasMorePages.value && !observer.value) {
      setupIntersectionObserver();
    }
  });
});

watch(
  () => props.currentGeoLocation,
  (newCoordinate) => {
    if (newCoordinate) {
      setLocation({
        latitude: newCoordinate.lat,
        longitude: newCoordinate.lng,
      });
    }
  },
);
</script>

<template>
  <div :class="[css(''), business.isMobileApp && css('', 'mobile-app')]">
    <div>
      <SatFormControl
        data-test="search"
        :class="css('search')"
        :label="t('components.pickup_locations.search_label')"
      >
        <div :class="css('search-content')">
          <div :class="css('search-input')">
            <AtomInput
              autofocus
              :value="search"
              :placeholder="t('components.pickup_locations.search_placeholder')"
              @keyup="(value) => (search = value)"
            />
          </div>
          <SatButtonCurrentLocation
            :api-key="googleMapsApiKey"
            :is-loading="isLoadingGeolocation"
            @success="onCurrentLocation"
            @click="emits('recenterMap')"
          />
        </div>
      </SatFormControl>
    </div>

    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <div v-else :class="css('content')">
      <WoLocation
        v-for="(item, index) in locations"
        :key="item.outletId"
        :class="css('location')"
        :name="item.name"
        :image="item.photo"
        :address="item.location"
        :address-prefix="distanceFormatted(item.distance)"
        :badge-value="`${index + 1}`"
        :state="mapState[item.state]"
        :state-label="outletStateMap[item.state]"
        :state-description="
          item.state === 'open'
            ? t('components.pickup_locations.preparation', {
                prepStart: item.preparationMinutes,
                prepEnd: item.preparationMinutes + ETA_PREPARATION_MARGIN,
              })
            : ''
        "
        :is-active="props.clickedLocationId === item.outletId"
        @mouse-hover="onFocusLocation(item)"
        @mouse-out="onBlurLocation"
        @click="onClickLocation(item)"
      >
        <div v-if="hasPackaging" :class="css('packaging')">
          <AtomBadge
            size="small"
            emphasis="low"
            state="neutral"
            data-test="packaging-takeaway"
            :value="packagingLabel('takeaway')"
          />
          <AtomBadge
            v-if="item.acceptsPackaging"
            size="small"
            emphasis="low"
            state="neutral"
            data-test="packaging-eatin"
            :value="packagingLabel('eatin')"
          />
        </div>
        <div
          v-if="
            item.state !== 'closed' && props.clickedLocationId === item.outletId
          "
        >
          <SatButton
            :is-loading="props.isCtaLoading"
            @click="() => emits('selectedLocation', item)"
          >
            {{
              item.state === "open"
                ? t("components.pickup_locations.pickup_cta")
                : t("components.pickup_locations.schedule_cta")
            }}
          </SatButton>
        </div>
      </WoLocation>
      <AtomText v-if="!locations?.length" color="color-text-secondary">
        {{ t("components.pickup_locations.empty_message") }}
      </AtomText>
      <div
        v-if="hasMorePages && locations.length > 0"
        ref="loadMoreRef"
        data-test="load-more"
        :class="css('load-more')"
      >
        <AtomLoadingSpinner v-if="isLoadingMore" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pickup-locations {
  display: flex;
  gap: var(--spacing-md);
  flex-direction: column;

  &__content {
    min-height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__loading {
    height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__search-content {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
  }

  &__search-input {
    flex: 1;
  }

  &__packaging {
    display: flex;
    gap: var(--spacing-sm);
  }

  &__location {
    width: 100%;
    padding-inline: 0;
  }

  &__load-more {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: var(--spacing-sm);
  }

  &--mobile-app .pickup-locations__location {
    padding-inline: var(--spacing-md);
  }
}
</style>
